import { useRef, useState } from 'react';
import axios from 'axios';
// import {Mutex, withTimeout} from 'async-mutex';
import { useStateChanged } from './useChange';

export enum Status {
  Initial,
  Pending,
  Success,
  Failed
}

export enum Method {
  Get = 'get',
  Post = 'post',
  Put = 'put',
  Patch = 'patch'
}

type useAxiosProps = {
  url: string
  method?: Method
  token?: string | null | Promise<string>
  headers?: Record<string, any>
}

function useAxios<T>({
  url, method, token, headers,
}: useAxiosProps) {
  // const mutexWithTimeout = withTimeout(new Mutex(), 15000);

  const [data, setData] = useState<T | Record<string, any>>({}); // {} to avoid pre-call data error
  const [errors, setErrors] = useState<string | string[]>('');
  const [status, setStatus] = useState<Status>(Status.Initial);
  const [loading, setLoading] = useState<boolean>(false);
  const [mutexIndex, setMutexIndex] = useState(0);
  const [executed, setExecuted] = useState(false);
  const currentMutexIndex = useRef(mutexIndex);
  currentMutexIndex.current = mutexIndex;

  const callMethod = method ?? Method.Get;
  
  const actualExec = async(body: any = null) => {
    setStatus(Status.Pending);
    const t = await Promise.resolve(token);
    // console.log('execute', url, t);
    const options: any = t ? { headers: { ...headers, Authorization: `Bearer ${t}` } } : { headers };
    if (body) {
      options.data = body;
    }
    // console.log('useaxios', url, options);

    return axios({ method: callMethod, url, ...options })
      .then((res: any) => {
        // console.log('useaxios responded', res.data);
        setData(res.data);
        setStatus(Status.Success);
        return res.data;
      })
      .catch((err: any) => {
        console.error('useAxios error', err.response);
        setStatus(Status.Failed);
        if (err.response?.data?.error?.errors) {
          setErrors(err.response?.data?.error?.errors as string[])
        } else if (err.response?.data?.error?.message) {
          setErrors(err.response?.data?.error?.message);
        } else {
          setErrors(err.response?.data?.error ?? err.message);
        }
        throw err;
      }).finally(() => {
        setLoading(false);
        if(!executed) {
          setExecuted(true);
        }
      });
  };

  const [callFunc, setCallFunc] = useState<((index: number) => void) | null>();
  useStateChanged(() => {
    setLoading(true);
    setTimeout(() => {
      // console.log('useaxios timeout', mutexIndex, currentMutexIndex.current);
      callFunc!(currentMutexIndex.current);
    }, callMethod === Method.Get ? 0 : 1000);
  }, [mutexIndex], { prefix: 'useAxios' });

  const exec = async(body: any = null): Promise<T> => {
    const newMutex = mutexIndex + 1;
    // set loading immediately
    setLoading(true);
    return new Promise((resolve) => {
      setCallFunc(() => async(actualIndex: number) => {
        // console.log('useaxios callfunc', url, {newMutex, actualIndex}, body);
        if (newMutex === actualIndex) {
          // console.log('useaxios exec', url, newMutex, body);
          const res = await actualExec(body);
          resolve(res);
        } else {
          console.log('useaxios skipping', url, newMutex, actualIndex);
        }
      });
      setMutexIndex(newMutex);
    });
  }


  return {
    data, status, errors, exec, loading, executed,
  };
}

//http://oyen.local:3005/eligibility/thirdparty?name=xyz&email=james@oyen.my&contact=0123221812&petName=bubu&petType=DOG

export default useAxios;
